import { useCallback, useEffect, useState } from 'react';

const usePhotoSelection = (jobId) => {
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [approvedPhotos, setApprovedPhotos] = useState(new Set());

  useEffect(() => {
    setSelectedPhotos(new Set());
    setExpandedCards(new Set());
    setApprovedPhotos(new Set());
  }, [jobId]);

  const togglePhotoSelection = useCallback((photoId) => {
    setSelectedPhotos((previous) => {
      const nextSelection = new Set(previous);
      if (nextSelection.has(photoId)) {
        nextSelection.delete(photoId);
      } else {
        nextSelection.add(photoId);
      }
      return nextSelection;
    });
  }, []);

  const toggleCardExpansion = useCallback((photoId) => {
    setExpandedCards((previous) => {
      const next = new Set(previous);
      if (next.has(photoId)) {
        next.delete(photoId);
      } else {
        next.add(photoId);
      }
      return next;
    });
  }, []);

  const setExpandedPhotoCards = useCallback((photoIds) => {
    setExpandedCards(new Set(photoIds));
  }, []);

  const approvePhoto = useCallback((photoId) => {
    setApprovedPhotos((previous) => new Set([...previous, photoId]));
    setSelectedPhotos((previous) => new Set([...previous, photoId]));
  }, []);

  const rejectPhoto = useCallback((photoId) => {
    setApprovedPhotos((previous) => {
      const next = new Set(previous);
      next.delete(photoId);
      return next;
    });

    setSelectedPhotos((previous) => {
      const next = new Set(previous);
      next.delete(photoId);
      return next;
    });
  }, []);

  const autoSelectPhotos = useCallback((jobData) => {
    if (!jobData?.results) {
      return;
    }

    const photoIdsToSelect = jobData.results
      .filter(
        (result) =>
          result.status === 'processed' &&
          !result.applied &&
          !(result.originalTitle && result.originalDescription)
      )
      .map((result) => result.photoId);

    setSelectedPhotos(new Set(photoIdsToSelect));
  }, []);

  const selectAllApplicablePhotos = useCallback((job) => {
    if (!job?.results) {
      return [];
    }

    const applicablePhotoIds = job.results
      .filter((result) => result.status === 'processed' && !result.applied)
      .map((result) => result.photoId);

    setSelectedPhotos(new Set(applicablePhotoIds));
    return applicablePhotoIds;
  }, []);

  const deselectAllPhotos = useCallback(() => {
    setSelectedPhotos(new Set());
  }, []);

  const bulkApproveAll = useCallback((job) => {
    if (!job?.results) {
      return [];
    }

    const processedPhotoIds = job.results
      .filter((result) => result.status === 'processed' && !result.applied)
      .map((result) => result.photoId);

    const processedSet = new Set(processedPhotoIds);
    setApprovedPhotos(processedSet);
    setSelectedPhotos(processedSet);

    return processedPhotoIds;
  }, []);

  return {
    selectedPhotos,
    setSelectedPhotos,
    expandedCards,
    approvedPhotos,
    setExpandedPhotoCards,
    togglePhotoSelection,
    toggleCardExpansion,
    approvePhoto,
    rejectPhoto,
    autoSelectPhotos,
    selectAllApplicablePhotos,
    deselectAllPhotos,
    bulkApproveAll
  };
};

export default usePhotoSelection;
